import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';

interface ProfileImageProps {
  size?: number;
  className?: string;
}

// Styled components
const ProfileContainer = styled.div`
  position: relative;
  display: inline-flex;
  
  @media (max-width: 768px) {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
    position: absolute !important;
    height: 0 !important;
    width: 0 !important;
    overflow: hidden !important;
  }
`;

const ProfilePicture = styled.div<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 60%;
    height: 60%;
    color: var(--brand-text-muted);
  }
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1000;
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    right: 10px;
    width: 10px;
    height: 10px;
    background: white;
    transform: rotate(45deg);
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    border-left: 1px solid rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #333;
  text-decoration: none;
  font-size: 0.9rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
    margin-right: 0.75rem;
    opacity: 0.7;
  }
  
  &:hover svg {
    opacity: 1;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  color: #ff4d4f;
  border: none;
  background: none;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 77, 79, 0.05);
  }
  
  svg {
    width: 18px;
    height: 18px;
    margin-right: 0.75rem;
    opacity: 0.7;
  }
  
  &:hover svg {
    opacity: 1;
  }
`;

const Initials = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const UserInfo = styled.div`
  padding: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  margin: 0.5rem 0 0.25rem;
`;

const UserEmail = styled.div`
  font-size: 0.8rem;
  color: #666;
`;

const ProfileImage: React.FC<ProfileImageProps> = ({ size = 36, className }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  
  // Add useState for tracking window width
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  
  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);
  
  // If in mobile view or not authenticated, don't render anything
  if (windowWidth <= 768 || !isAuthenticated || !user) {
    return null;
  }
  
  // We're always using the person icon now instead of initials
  const getInitials = () => {
    // Always return null to ensure we use the person icon
    return null;
  };
  
  return (
    <ProfileContainer ref={containerRef} className={className}>
      <ProfilePicture size={size} onClick={toggleDropdown}>
        {user.profileImage ? (
          <img
            src={user.profileImage}
            alt={user.name}
            loading="lazy"
            width={size}
            height={size}
          />
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        )}
      </ProfilePicture>
      
      <Dropdown $isOpen={dropdownOpen}>
        {location.pathname !== '/desktop' && (
          <DropdownItem to="/desktop">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            desktop
          </DropdownItem>
        )}

        {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
            fail-open (guests / legacy accounts unaffected), so a member the
            admin restricted doesn't get a dropdown path to the access wall. */}
        {canSeeWallet && location.pathname !== '/wallet' && (
          <DropdownItem to="/wallet">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M16 12h.01"/>
              <path d="M2 10h20"/>
            </svg>
            wallet
          </DropdownItem>
        )}

        {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
            fail-open. Menu order convention: activity always sits between
            wallet and profile. */}
        {canSeeActivity && location.pathname !== '/activity' && (
          <DropdownItem to="/activity">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18"/>
              <path d="M7 15l4-4 3 3 5-6"/>
            </svg>
            activity
          </DropdownItem>
        )}

        {location.pathname !== '/profile' && (
          <DropdownItem to="/profile">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            profile
          </DropdownItem>
        )}
        
        {location.pathname !== '/settings' && (
          <DropdownItem to="/settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            settings
          </DropdownItem>
        )}

        {!location.pathname.startsWith('/settings/team') && (
          <DropdownItem to="/settings/team">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            team & roles
          </DropdownItem>
        )}

        {/* Multi-org (owner directive 2026-08-16): the organization editor was
            only reachable through a link buried in /profile — it is a
            first-class page and belongs in this menu. */}
        {location.pathname !== '/organization' && (
          <DropdownItem to="/organization">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18"/>
              <path d="M5 21V7l7-4 7 4v14"/>
              <path d="M9 21v-6h6v6"/>
            </svg>
            organization
          </DropdownItem>
        )}

        {!location.pathname.startsWith('/integrations') && (
          <DropdownItem to="/integrations">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            integrations
          </DropdownItem>
        )}

        {/* Task #77: billing surfaces share one visibility rule — owner or
            billing.view (same gate as the wallet entry; fail-open until RBAC
            resolves). Members without it don't get the menu entry at all;
            a direct URL still lands on the honest wall with the
            notify-the-owner alternative. */}
        {canSeeWallet && location.pathname !== '/subscription' && (
          <DropdownItem to="/subscription">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
            subscription
          </DropdownItem>
        )}
        
        <LogoutButton onClick={() => logout()}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          logout
        </LogoutButton>
      </Dropdown>
    </ProfileContainer>
  );
};

export default ProfileImage; 